import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart3, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { RiskFindingCard } from "@/components/RiskFindingCard";
import { JourneyTimeline } from "@/components/JourneyTimeline";
import { OutcomeCard } from "@/components/OutcomeCard";
import { StageNotReached } from "@/components/StageNotReached";
import { Card, Spinner, Badge } from "@/components/ui/primitives";
import { markStageForPlayback } from "@/lib/playback";
import { usePlayback } from "@/hooks/usePlayback";
import { useAutoAdvance } from "@/hooks/useAutoAdvance";

export const Route = createFileRoute("/apps/$id/analysis")({ component: AnalysisPage });

function AnalysisPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["analysis", id], queryFn: () => api.getAnalysis(id) });
  const { data: app } = useQuery({ queryKey: ["application", id], queryFn: () => api.getApplication(id) });

  const advance = useMutation({
    mutationFn: () => api.advance(id),
    onSuccess: async () => {
      markStageForPlayback(id, "analysis");
      await qc.invalidateQueries();
    },
  });
  const advanceNext = useMutation({
    mutationFn: () => api.advance(id),
    onSuccess: async () => {
      markStageForPlayback(id, "coordination");
      await qc.invalidateQueries();
      navigate({ to: "/apps/$id/coordination", params: { id } });
    },
  });
  const rerun = useMutation({ mutationFn: () => api.reRunStage(id, "analysis"), onSuccess: () => qc.invalidateQueries() });

  const playback = usePlayback({
    stage: "analysis",
    appId: id,
    events: data?.journey_events ?? [],
    enabled: Boolean(data?.reached),
    cardCount: data?.findings.length ?? 0,
  });

  useAutoAdvance({
    appId: id,
    stage: "analysis",
    isCurrent: app?.stage === "analysis",
    playbackStatus: playback.status,
    advanceMutation: advanceNext,
    nextStage: "coordination",
  });

  if (isLoading) return <div className="flex items-center gap-2 text-muted-foreground"><Spinner /> Loading analysis…</div>;
  if (!data) return null;
  if (!data.reached) {
    return (
      <StageNotReached
        title="Analysis hasn't run yet"
        description="The Analyst Agent and its supporting bureau / income / affordability / fraud agents run when the application moves from Intake to Analysis. Advance the pipeline to produce the risk findings."
        primaryLabel="Run analysis"
        onPrimary={() => advance.mutate()}
        primaryDisabled={advance.isPending}
      />
    );
  }
  const isCurrent = app?.stage === "analysis";

  const summary = pickSummaryMetrics(data.extracted_fields);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
      <div className="space-y-6">
        <header>
          <h2 className="flex items-center gap-2 text-xl font-semibold">
            <BarChart3 className="h-5 w-5 text-warn" strokeWidth={1.9} /> Risk analysis
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">The Analyst Agent reviews extracted data against the Credit Policy Manual.</p>
        </header>

        <Card className="p-5">
          <div className="text-[0.72rem] uppercase tracking-wider text-muted-foreground">Extracted data summary</div>
          <div className="mt-3 grid grid-cols-2 gap-6 sm:grid-cols-4">
            {summary.map((s) => (
              <div key={s.label}>
                <div className="text-sm text-muted-foreground">{s.label}</div>
                <div className="text-lg font-semibold tabular-nums">{s.value}</div>
              </div>
            ))}
          </div>
        </Card>

        <section className="space-y-3">
          {!playback.revealFindings ? (
            <Card className="flex items-center gap-3 p-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin text-warn" strokeWidth={2.2} />
              Waiting for agents to finish before surfacing findings…
            </Card>
          ) : data.findings.length === 0 ? (
            <Card className="p-4 text-sm text-muted-foreground">No risk findings recorded.</Card>
          ) : (
            data.findings.map((f, i) => (
              <RiskFindingCard
                key={f.id}
                finding={f}
                animateIn={playback.isPlaying}
                delayMs={playback.cardDelayMs(i)}
              />
            ))
          )}
        </section>

        <section>
          <h3 className="mb-3 text-base font-semibold">Agent activity</h3>
          <JourneyTimeline events={data.journey_events} playback={playback} />
        </section>
      </div>

      {playback.revealOutcome ? (
        <OutcomeCard
          tone="warn"
          title="Risk Assessment Complete"
          primaryLabel={isCurrent ? "Continue to Coordination →" : "Go to Coordination →"}
          onPrimary={() => (isCurrent ? advanceNext.mutate() : navigate({ to: "/apps/$id/coordination", params: { id } }))}
          primaryDisabled={advanceNext.isPending}
          secondaryLabel={isCurrent ? "Re-run analysis" : undefined}
          onSecondary={isCurrent ? () => rerun.mutate() : undefined}
        >
          {data.risk_tier && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Risk tier</span>
              <Badge variant="default">Tier {data.risk_tier}</Badge>
            </div>
          )}
          <div className="pt-1">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              Findings ({data.findings.length})
            </div>
            {(() => {
              const warnCount = data.findings.filter((f) => f.severity === "warn").length;
              const critCount = data.findings.filter((f) => f.severity === "critical").length;
              if (warnCount === 0 && critCount === 0) return null;
              const parts: string[] = [];
              if (warnCount > 0) parts.push(`${warnCount} warning${warnCount === 1 ? "" : "s"}`);
              if (critCount > 0) parts.push(`${critCount} critical`);
              return (
                <div className="mt-0.5 text-[0.7rem] text-muted-foreground/80">
                  {parts.join(" · ")}
                </div>
              );
            })()}
            <ul className="mt-2 space-y-1">
              {data.findings.map((f) => (
                <li key={f.id} className="flex items-start gap-2">
                  <span className={`mt-1.5 h-1.5 w-1.5 rounded-full ${f.severity === "warn" ? "bg-warn" : f.severity === "critical" ? "bg-destructive" : "bg-brand"}`} />
                  <span className="text-sm">{f.title}</span>
                </li>
              ))}
            </ul>
          </div>
        </OutcomeCard>
      ) : (
        <aside className="sticky top-8 rounded-xl border border-warn/30 bg-warn/5 p-5">
          <div className="flex items-center gap-2 text-sm font-semibold text-warn">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.2} />
            Running analysis…
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            Bureau, Income Verification, Affordability and Fraud Signals agents are
            working in parallel. The Analyst Agent will synthesise their outputs into
            a tier and flagged findings once they complete.
          </p>
        </aside>
      )}
    </div>
  );
}

function pickSummaryMetrics(fields: { key: string; label: string; value: string }[]) {
  const pick = (key: string) => fields.find((f) => f.key === key)?.value ?? "—";
  return [
    { label: "Bureau score", value: pick("bureau_score") },
    { label: "Monthly income", value: pick("monthly_gross_income") },
    { label: "Employer", value: pick("employer") },
    { label: "Address verified", value: pick("address") },
  ];
}
